import {
  isAuthSessionMutationCurrent,
  type AuthSessionTransitionPermit,
  type AuthTokenResponse,
} from "./session";
import {
  stageCurrentAnonymousStorageForNewAccount,
  type AnonymousAccountStorageMigrationIntent,
} from "@/shared/storage/account-scope";
import {
  hasExactCreatedAccountBootstrapAuthority,
  withExactCreatedAccountBootstrapAuthority,
} from "./created-account-response.mjs";

type ApplyTokenResponse = (data: AuthTokenResponse, permit: AuthSessionTransitionPermit) => boolean;

/**
 * Applies a creation response and its anonymous browser data as one local
 * transaction. Exact account-creation AND creating-device bootstrap authority
 * are mandatory; neither discriminator may substitute for the other.
 */
export function applyCreatedAccountSession(
  data: AuthTokenResponse,
  permit: AuthSessionTransitionPermit,
  migrationIntent: AnonymousAccountStorageMigrationIntent | null,
  applyTokenResponse: ApplyTokenResponse,
): boolean {
  if (!isAuthSessionMutationCurrent(permit) || !hasExactCreatedAccountBootstrapAuthority(data)) {
    return false;
  }
  return withExactCreatedAccountBootstrapAuthority(data, () => {
    const migration = stageCurrentAnonymousStorageForNewAccount(migrationIntent, data.user);
    if (
      migrationIntent &&
      migration.outcome !== "staged" &&
      migration.outcome !== "bootstrap_pending" &&
      migration.outcome !== "server_completed"
    ) {
      return false;
    }
    try {
      if (!isAuthSessionMutationCurrent(permit) || !applyTokenResponse(data, permit)) {
        return false;
      }
      migration?.commit();
      return true;
    } finally {
      // A committed transaction is already inactive. Any rejected/superseded
      // token response removes every staged target write while retaining anon.
      migration?.rollback();
    }
  });
}

/**
 * Existing-account sign-in never adopts anonymous data on a token response.
 * A true creating-device recovery fence is handled separately by the
 * authenticated `/me` authority probe before cloud pull.
 */
export function applyAuthenticatedOrCreatedAccountSession(
  data: AuthTokenResponse,
  permit: AuthSessionTransitionPermit,
  migrationIntent: AnonymousAccountStorageMigrationIntent | null,
  applyTokenResponse: ApplyTokenResponse,
): boolean {
  if (data.accountCreated === true) {
    return applyCreatedAccountSession(data, permit, migrationIntent, applyTokenResponse);
  }
  return isAuthSessionMutationCurrent(permit) && applyTokenResponse(data, permit);
}
